import { useState } from 'react';
import { useParams, Link } from 'react-router';
import { PenLine, Signature } from 'lucide-react';
import { toast } from 'sonner';
import { DetailPageStatus } from '@/components/detail/DetailPageStatus';
import { signTimeSheet } from '@/components/timesheet/signTimeSheet';
import { useAbsencesToSign } from '@/stores/useAbsencesToSign';
import { TimeSheetDetailView } from '@/components/timesheet/TimeSheetDetail';
import { loadTimeSheetDetail } from '@/components/timesheet/timeSheetDetailData';
import { Button } from '@/components/ui/button';
import { useFollowEntityCompany } from '@/hooks/useFollowEntityCompany';
import { useLoader } from '@/hooks/useLoader';
import { TIME_SHEET_SIGNATURE_TYPE } from '@/models/references/references';
import { SignDialog } from '@/components/timesheet/SignDialog';

const BACK = { to: '/requests', label: 'Toutes mes demandes' };

/**
 * Détail d'une demande de l'employé (/requests/{id}) : sa feuille d'une période (TimeSheetDetailView, partagé avec la
 * Validation). Un brouillon (pas encore soumis, ou refusé : motif dans l'historique) renvoie vers la Feuille d'heure.
 * Une absence (déclarée par le manager ou le Dirigeant, validée sans l'employé) attend sa signature : « Signer mon absence ».
 */
export function RequestDetailPage() {
  const { id = '' } = useParams();
  const { data, error, loaded, reload } = useLoader(id, loadTimeSheetDetail);
  const [signing, setSigning] = useState(false);

  useFollowEntityCompany(data?.period.companyId, '/requests');

  if (!data) {
    return <DetailPageStatus back={BACK} loaded={loaded} error={error} notFound="Demande introuvable." />;
  }
  const toSign = data.sheet.isAbsence && !data.signatures.employee;

  return (
    <>
      <TimeSheetDetailView
        data={data}
        back={BACK}
        title={data.period.monthLabel}
        actions={
          data.sheet.isDraft ? (
            <Button asChild variant="outline">
              <Link to="/timesheet">
                <PenLine />
                Compléter ma feuille
              </Link>
            </Button>
          ) : (
            toSign && (
              <Button onClick={() => setSigning(true)}>
                <Signature />
                Signer mon absence
              </Button>
            )
          )
        }
      />
      {signing && (
        <SignDialog
          title="Signer mon absence"
          description={
            <>
              Une absence a été déclarée pour vous en {data.period.monthLabel.toLowerCase()} : aucune heure
              supplémentaire sur cette période. En signant, vous confirmez cette déclaration.
            </>
          }
          confirmLabel="Signer"
          confirmIcon={Signature}
          onSign={(image) => signTimeSheet(data.sheet.urn, TIME_SHEET_SIGNATURE_TYPE.EMPLOYEE, image)}
          onDone={() => {
            setSigning(false);
            toast.success('Votre absence est signée.');
            reload();
            // Compteur de Mes Demandes, encart
            void useAbsencesToSign.getState().refresh();
          }}
          onClose={() => setSigning(false)}
        />
      )}
    </>
  );
}
